import type {PublicationContext, Subscription} from 'centrifuge';
import {SubscriptionState} from 'centrifuge';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {PosterCollaboration} from '@PosterWhiteboard/collaboration/poster-collaboration.class';
import type {PosterCollaborationConnection} from '@PosterWhiteboard/collaboration/poster-collaboration-connection.class';
import type {CanvasPoint, PresenceMessage, PresencePayload} from '@PosterWhiteboard/collaboration/poster-presence.types';
import {
  PRESENCE_HEARTBEAT_INTERVAL_MS,
  PRESENCE_PUBLISH_INTERVAL_MS,
  PresenceMessageType,
  REMOTE_SESSION_PRUNE_INTERVAL_MS,
} from '@PosterWhiteboard/collaboration/poster-presence.types';
import {isCollaborationParticipant, isPresenceMessage} from '@PosterWhiteboard/collaboration/presence-sessions.library';

interface ClientPoint {
  clientX: number;
  clientY: number;
}

interface PointerOnPage {
  pageId: string;
  point: CanvasPoint;
}

const CANVAS_COORDINATE_PRECISION = 10;

/**
 * Tells the other tabs editing this poster where this tab is, and keeps the Redux mirror of
 * where they are. Every tab publishes its page, pointer and selection on the poster's presence
 * channel whenever they change (plus a heartbeat); the overlay and the nav-bar roster render
 * what comes back. Pointer positions travel in page canvas coordinates, so each viewer projects
 * them through its own zoom.
 *
 * Joined and left by PosterCollaboration, which owns the connection.
 */
export class PosterPresence {
  #connection: PosterCollaborationConnection | null = null;
  #subscription: Subscription | null = null;
  #publishTimer: ReturnType<typeof setInterval> | null = null;
  #pruneTimer: ReturnType<typeof setInterval> | null = null;
  #pointer: ClientPoint | null = null;
  #lastPublishedState = '';
  #lastPublishedAt = 0;
  readonly #onPointerMove = (event: PointerEvent): void => {
    this.#pointer = {clientX: event.clientX, clientY: event.clientY};
  };
  readonly #onPointerLeave = (): void => {
    this.#pointer = null;
  };
  readonly #onPageHide = (): void => {
    void this.leave();
  };

  constructor(
    private readonly poster: Poster,
    private readonly collaboration: PosterCollaboration
  ) {}

  public join(connection: PosterCollaborationConnection): void {
    this.#connection = connection;
    this.#subscription = connection.subscribe('presence', this.#onPublication.bind(this));
    this.#startTracking();
  }

  /** Tells the others this tab is gone and unsubscribes once that message is out; resolves when the channel is left. */
  public leave(): Promise<void> {
    this.#stopTracking();
    this.poster.redux.resetPosterPresence();
    return this.#leaveChannel();
  }

  #startTracking(): void {
    this.poster.HTMLElement.addEventListener('pointermove', this.#onPointerMove);
    this.poster.HTMLElement.addEventListener('pointerleave', this.#onPointerLeave);
    window.addEventListener('pagehide', this.#onPageHide);
    this.#publishTimer = setInterval(this.#publishWhenChanged.bind(this), PRESENCE_PUBLISH_INTERVAL_MS);
    this.#pruneTimer = setInterval((): void => {
      this.poster.redux.pruneStaleRemoteSessions(Date.now());
    }, REMOTE_SESSION_PRUNE_INTERVAL_MS);
  }

  #stopTracking(): void {
    this.poster.HTMLElement.removeEventListener('pointermove', this.#onPointerMove);
    this.poster.HTMLElement.removeEventListener('pointerleave', this.#onPointerLeave);
    window.removeEventListener('pagehide', this.#onPageHide);
    if (this.#publishTimer !== null) {
      clearInterval(this.#publishTimer);
      this.#publishTimer = null;
    }
    if (this.#pruneTimer !== null) {
      clearInterval(this.#pruneTimer);
      this.#pruneTimer = null;
    }
    this.#pointer = null;
    this.#lastPublishedState = '';
    this.#lastPublishedAt = 0;
  }

  /** Publishes the leave message before unsubscribing: unsubscribing first would discard it. */
  async #leaveChannel(): Promise<void> {
    const connection = this.#connection;
    const subscription = this.#subscription;
    if (connection === null || subscription === null) {
      return;
    }
    this.#connection = null;
    this.#subscription = null;
    if (subscription.state === SubscriptionState.Subscribed) {
      await subscription.publish({type: PresenceMessageType.LEAVE, sessionId: this.collaboration.sessionId} satisfies PresenceMessage).catch(ignoreLostLeaveMessage);
    }
    connection.unsubscribe(subscription);
  }

  #publishWhenChanged(): void {
    const payload = this.#buildPayload();
    const state = JSON.stringify(payload);
    const now = Date.now();
    const isHeartbeatDue = now - this.#lastPublishedAt >= PRESENCE_HEARTBEAT_INTERVAL_MS;
    if (state === this.#lastPublishedState && !isHeartbeatDue) {
      return;
    }
    this.#publish({type: PresenceMessageType.UPDATE, ...payload});
    this.#lastPublishedState = state;
    this.#lastPublishedAt = now;
  }

  #buildPayload(): PresencePayload {
    const pointerOnPage = this.#getPointerOnPage();
    const pageId = pointerOnPage?.pageId ?? this.poster.getCurrentPageId();
    return {
      sessionId: this.collaboration.sessionId,
      pageId,
      cursor: pointerOnPage?.point ?? null,
      selectedItemIds: this.poster.pages.pagesHashMap[pageId]?.getSelectedItems().map((item) => item.uid) ?? [],
    };
  }

  /** The page under the pointer and the pointer's position on it in canvas pixels, through whatever CSS scale the page is shown at. */
  #getPointerOnPage(): PointerOnPage | null {
    if (this.#pointer === null) {
      return null;
    }
    for (const page of Object.values(this.poster.pages.pagesHashMap)) {
      const rect = page.canvasBackdrop.getBoundingClientRect();
      const isOverPage = rect.width > 0 && this.#pointer.clientX >= rect.left && this.#pointer.clientX <= rect.right && this.#pointer.clientY >= rect.top && this.#pointer.clientY <= rect.bottom;
      if (isOverPage) {
        return {
          pageId: page.hashedID,
          point: {
            x: roundCanvasCoordinate(((this.#pointer.clientX - rect.left) / rect.width) * this.poster.width),
            y: roundCanvasCoordinate(((this.#pointer.clientY - rect.top) / rect.height) * this.poster.height),
          },
        };
      }
    }
    return null;
  }

  #publish(message: PresenceMessage): void {
    if (this.#subscription?.state !== SubscriptionState.Subscribed) {
      return;
    }
    void this.#subscription.publish(message).catch(this.#republishOnNextTick.bind(this));
  }

  /** A publish lost mid-reconnect is not an error worth surfacing: the next tick sends the current state again. */
  #republishOnNextTick(): void {
    this.#lastPublishedState = '';
  }

  #onPublication(context: PublicationContext): void {
    const message: unknown = context.data;
    if (!isPresenceMessage(message) || message.sessionId === this.collaboration.sessionId) {
      return;
    }
    const participant: unknown = context.info?.chanInfo;
    if (!isCollaborationParticipant(participant)) {
      return;
    }
    if (message.type === PresenceMessageType.LEAVE) {
      this.poster.redux.remoteSessionLeft({sessionId: message.sessionId, participant});
      return;
    }
    const {type: _type, ...payload} = message;
    this.poster.redux.remoteSessionSeen({payload, participant, now: Date.now()});
  }
}

const ignoreLostLeaveMessage = (): void => {
  // The others drop a silent session after the stale timeout anyway.
};

const roundCanvasCoordinate = (value: number): number => Math.round(value * CANVAS_COORDINATE_PRECISION) / CANVAS_COORDINATE_PRECISION;
